<template>
    <div class="flex flex-col w-full rounded-lg text-white bg-gray-950/50 overflow-hidden">
        <div class="shrink-0 p-6 pb-5 space-y-4 border-b border-gray-700 bg-gray-950/25">
            <div class="flex items-center space-x-4">
                <Avatar 
                    modal
                    size="xl"
                    class="rounded-lg flex-shrink-0 ring-1 ring-gray-600"
                    :path="customer.avatar" 
                    :placeholder="[customer.first_name, customer.last_name].filter(Boolean).join(' ')" 
                />
                <div class="flex-1">
                    <p class="text-xl font-medium text-white">{{ customerName }}</p>
                    <p v-if="!!customerDemographics" class="capitalize font-light text-gray-200">{{ customerDemographics }}</p>
                </div>
            </div>

            <UButton @click="emit('switch')" size="sm" color="white" block icon="i-ph-arrows-left-right-bold">
                Switch Customer
            </UButton>
        </div>
        <div class="flex-1 p-6 space-y-5 font-light overflow-auto">
            <div class="flex items-center justify-between space-x-4">
                <div>
                    <p class="text-xs text-gray-400 uppercase">Subscription</p>
                    <BadgeSubscriptionStatus with-expiry :subscription="activeSubscription" class="mt-1.5" />
                </div>
                <UButton v-if="!activeSubscription || isSubscriptionExpired" @click="emit('subscribe')" size="sm" color="white" icon="i-ph-shield-bold">
                    Subscribe
                </UButton>
            </div>
            <div v-if="activeSubscription?.partner">
                <p class="text-xs text-gray-400 uppercase">Partner</p>
                <div class="flex items-center space-x-2.5 mt-1.5">
                    <Avatar bucket="media" folder="partners" size="sm" :placeholder="activeSubscription.partner.name" :path="activeSubscription.partner.avatar" />
                    <span>{{ activeSubscription.partner.name }}</span>
                </div>
            </div>
            <div v-if="customer?.policy_holder">
                <p class="text-xs text-gray-400 uppercase">Policy Holder</p>
                <p>{{ customer.policy_holder }}</p>
            </div>
            <div v-if="(switchedCallCount ?? 0) > 1" class="rounded-md bg-red-500/15 ring-1 ring-red-400/40 px-3 py-2">
                <p class="text-xs text-red-300 uppercase">Non-member switches</p>
                <p class="text-red-100">
                    This caller has had {{ switchedCallCount }} calls switched to non-members.
                </p>
            </div>
            <div v-if="exclusion">
                <p class="text-xs text-gray-400 uppercase">Exclusion</p>
                <TruncatedText :value="exclusion" />
            </div>
            <div v-if="customer.tags?.length">
                <p class="text-xs text-gray-400 uppercase">Tags</p>
                <div class="flex flex-wrap gap-1 mt-1">
                    <UBadge v-for="tag in customer.tags" :key="tag" :label="tag" color="gray" />
                </div>
            </div>
            <div v-if="customer.phone || customer.metadata?.alternate_phone" class="grid grid-cols-2 gap-4">
                <div v-if="customer.phone" class="col-span-1">
                    <p class="text-xs text-gray-400 uppercase">Phone Number</p>
                    <PhoneLabel :value="customer.phone" />
                </div>
                <div v-if="customer.metadata?.alternate_phone" class="col-span-1">
                    <p class="text-xs text-gray-400 uppercase">Alternate Phone</p>
                    <PhoneLabel :value="customer.metadata.alternate_phone" />
                </div>
            </div>
            <div v-if="customer.metadata?.emergency_contact || customer.metadata?.emergency_phone">
                <p class="text-xs text-gray-400 uppercase">Emergency Contact</p>
                <div class="flex items-center space-x-2">
                    <PhoneLabel v-if="customer.metadata?.emergency_phone" :value="customer.metadata?.emergency_phone" class="shrink-0" />
                    <p v-if="customer.metadata?.emergency_contact" class="truncate">
                        <span v-if="customer.metadata?.emergency_phone">– </span>
                        <span>{{ customer.metadata?.emergency_contact }}</span>
                    </p>
                </div>
            </div>
            <div v-if="customer.chronic_diseases">
                <p class="text-xs text-gray-400 uppercase">Chronic Diseases</p>
                <p class="whitespace-pre-wrap">{{ customer.chronic_diseases || '--' }}</p>
            </div>
            <div v-if="customer.chronic_medication">
                <p class="text-xs text-gray-400 uppercase">Chronic Medication</p>
                <p class="whitespace-pre-wrap">{{ customer.chronic_medication || '--' }}</p>
            </div>
            <div v-if="customer.surgeries">
                <p class="text-xs text-gray-400 uppercase">Past Surgeries</p>
                <p class="whitespace-pre-wrap">{{ customer.surgeries || '--' }}</p>
            </div>
            <div v-if="customer.allergies">
                <p class="text-xs text-gray-400 uppercase">Allergies</p>
                <p class="whitespace-pre-wrap">{{ customer.allergies || '--' }}</p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Customer } from '~/types/customer'

const emit = defineEmits<{
    'switch': [void]
    'subscribe': [void]
}>()

const props = defineProps<{
    customer: Customer
    exclusion?: string
    switchedCallCount?: number
}>()

const { age } = useNumber()

const customerName = computed(() => {
    if (props.customer) {
        return [props.customer.first_name, props.customer.middle_name, props.customer.last_name].filter(Boolean).join(' ')
    }
})

const customerDemographics = computed(() => {
    if (props.customer) {
        return [
            props.customer.gender, 
            age(props.customer.birth_date),
            props.customer.blood_type
        ].filter(Boolean).join(' · ')
    }
})

const activeSubscription = computed(() => {
    if (props.customer.subscriptions?.length) {
        return props.customer.subscriptions[0]
    }
})

const isSubscriptionExpired = computed(() => {
    if (!activeSubscription.value?.expires_at) return false
    return new Date(activeSubscription.value.expires_at) < new Date()
})
</script>